import { defineStore } from "pinia";
import {
  LoadState,
  stateInitial,
  stateLoading,
  stateError,
  stateLoaded,
  PaginatedLoadState,
} from "../models/state";
import { onBeforeMount, ref } from "vue";
import {
  MaintenanceRule,
  MaintenanceRuleContent,
  MaintenanceRuleResponse,
  MaintenanceRulesQuery,
  SOP,
  SOPQuery,
  activateMaintenanceRule,
  deactivateMaintenanceRule,
  getMaintenanceRules,
  getSops,
  postMaintenanceRuleUpdate,
  postMaintenanceRules,
  patchMaintenanceRule,
  deleteMaintenanceRule,
} from "./service";
import { version } from "../preferences";
import { localStorageMachineRestrictionKey, statuses } from "./constants";
import axios, { AxiosRequestConfig } from "axios";
import { loadResource } from "@shared/util/loadResource";
import {
  MachineRestrictionResponse,
  searchMachineRestrictions,
} from "@shared/services/settings";
import MachineRestrictions from "./machineRestrictions/MachineRestrictions.vue";
import { update } from "lodash";

export const useMaintenanceStore = defineStore("maintenanceStore", () => {
  const rulesLoadingState = ref<PaginatedLoadState<MaintenanceRuleResponse[]>>({
    ...stateInitial,
    result: [],
  });

  async function loadRules(
    query: MaintenanceRulesQuery,
    axiosConfig?: AxiosRequestConfig,
  ) {
    rulesLoadingState.value = {
      ...rulesLoadingState.value,
      ...stateLoading,
    };
    const response = await getMaintenanceRules(query, axiosConfig).catch(
      (e) => {
        if (axios.isCancel(e)) {
          // Request was cancelled by the user, i.e. by starting a new request
          return;
        }
        rulesLoadingState.value = {
          ...rulesLoadingState.value,
          ...stateError,
        };
        throw e;
      },
    );
    if (response) {
      rulesLoadingState.value = { ...stateLoaded, ...response.data };
    }
  }

  async function createRule(rule: MaintenanceRuleContent) {
    await postMaintenanceRules(rule);
  }

  const ruleLoadingState = ref<LoadState<MaintenanceRuleResponse>>({
    ...stateInitial,
  });

  async function loadRule(id: string) {
    ruleLoadingState.value = { ...ruleLoadingState.value, ...stateLoading };
    const response = await getMaintenanceRules({ uuid: id }).catch((e) => {
      ruleLoadingState.value = {
        ...ruleLoadingState.value,
        ...stateError,
      };
      throw e;
    });
    if (response.data.result.length !== 1) {
      ruleLoadingState.value = {
        ...ruleLoadingState.value,
        ...stateError,
      };
      throw new Error("Error loading rule");
    }
    ruleLoadingState.value = {
      ...stateLoaded,
      result: response.data.result[0],
    };
  }

  async function editRule(id: string, rule: MaintenanceRuleContent) {
    const response = await patchMaintenanceRule(id, rule);
  }

  async function activateRule(id: string) {
    await activateMaintenanceRule(id).then((response) => {
      rulesLoadingState.value.result.find((item) =>
        item.versions.some((v) => v.uuid === id),
      ).versions[0] = response.data.versions[0];
    });
  }

  async function deactivateRule(id: string) {
    await deactivateMaintenanceRule(id).then(() => {
      rulesLoadingState.value.result.find((item) =>
        item.versions.some((v) => v.uuid === id),
      ).versions[0].active = false;
    });
  }

  async function deleteRule(id: string) {
    await deleteMaintenanceRule(id);
    rulesLoadingState.value.result.splice(
      rulesLoadingState.value.result.findIndex((item) => item.uuid === id),
      1,
    );
  }

  const sopsLoadingState = ref<PaginatedLoadState<SOP[]>>({
    ...stateInitial,
    result: [],
  });

  async function loadSops(query: SOPQuery) {
    sopsLoadingState.value = {
      ...stateLoading,
      result: sopsLoadingState.value.result,
    };
    const result = await getSops(query).catch((e) => {
      sopsLoadingState.value = { ...stateError };
      throw e;
    });
    sopsLoadingState.value = { ...stateLoaded, ...result.data };
  }

  const calendarRange = ref(null);
  const calendarFilterStatus = ref(null);

  const search = ref("");
  const selectedUsers = ref<string[]>([]);

  const maintenancePlanSearch = ref("");

  return {
    rulesLoadingState,
    loadRules,
    createRule,
    ruleLoadingState,
    loadRule,
    editRule,
    activateRule,
    deactivateRule,
    deleteRule,
    loadSops,
    sopsLoadingState,
    calendarRange,
    calendarFilterStatus,
    search,
    maintenancePlanSearch,
    selectedUsers,
  };
});
